/**
 * SA Tools Constants
 * Default values and configuration
 * Updated to match ipa-frontend scoring system (0-4 scale)
 */

// Score maps for impact and importance levels
// Score formula: Σ(Impact × Importance) across all 4 categories
// Maximum score: 4 × (1.0 × 1.0) = 4.0
// Source: ipa-frontend constants.js
export const scoreMap = {
  'None': 0.0,
  'Low': 0.25,
  'Medium-Low': 0.5,
  'Medium-High': 0.75,
  'High': 1.0
};

// Backward compatibility alias
export const scoreMapping = scoreMap;

// Impact and importance levels
export const impactLevels = [
  { value: 'None', label: 'None', score: 0.0 },
  { value: 'Low', label: 'Low', score: 0.25 },
  { value: 'Medium-Low', label: 'Med-Low', score: 0.5 },
  { value: 'Medium-High', label: 'Med-High', score: 0.75 },
  { value: 'High', label: 'High', score: 1.0 }
];

// Complexity levels
export const complexityLevels = [
  { value: 'Very Low', label: 'Very Low', score: 1 },
  { value: 'Low', label: 'Low', score: 2 },
  { value: 'Medium', label: 'Medium', score: 3 },
  { value: 'High', label: 'High', score: 4 },
  { value: 'Custom', label: 'Custom', score: 5 }
];

// Map complexity values to scores
export const complexityScoreMap = {
  'Very Low': 1,
  'Low': 2,
  'Medium': 3,
  'High': 4,
  'Custom': 5
};

// Category types for assessment
export const categories = [
  { key: 'productivity', label: 'Productivity / Efficiency' },
  { key: 'customer', label: 'Customer Experience' },
  { key: 'agent', label: 'Agent Experience' },
  { key: 'compliance', label: 'Compliance' }
];

// Rating options for dropdowns
export const ratingOptions = [
  { value: 'None', label: 'None' },
  { value: 'Low', label: 'Low' },
  { value: 'Medium-Low', label: 'Medium-Low' },
  { value: 'Medium-High', label: 'Medium-High' },
  { value: 'High', label: 'High' }
];

// Complexity options
export const complexityOptions = [
  { value: 'Very Low', label: 'Very Low' },
  { value: 'Low', label: 'Low' },
  { value: 'Medium', label: 'Medium' },
  { value: 'High', label: 'High' },
  { value: 'Custom', label: 'Custom' }
];

// Reverse score mapping (for display)
export const scoreToLabel = (score) => {
  if (score === 0 || score === 0.0) return 'None';
  if (score === 0.25) return 'Low';
  if (score === 0.5) return 'Medium-Low';
  if (score === 0.75) return 'Medium-High';
  if (score === 1.0 || score === 1) return 'High';
  return 'None';
};

// Default context structure
export const defaultContext = {
  customer: '',
  vertical: '',
  lob: '',
  ipa_date: '',
  cost_per_agent: 25,
  professional_services_price: 0,
  bom_price: 0,
  bom_length_months: 0,
  monthly_usage_costs: 0,
  proposal_team: [],
  introduction: `Presidio conducted an Interaction Process Automation workshop on-site at CUSTOMER in DATE. The purpose of this engagement was to perform a deep-dive into existing customer and employee interactions, identify automation opportunities, and explore strategies that could significantly impact customer experience, agent and supervisor efficiency, and overall operational complexity.

During the workshop, Presidio reviewed workflows and processes for four key operational groups: LINES OF BUSINESSS - LOB. These groups represent critical touchpoints for CUSTOMER's students, faculty, and staff, handling a diverse range of interactions from technical support to emergency services. Through collaborative sessions with IT and business stakeholders, Presidio gathered valuable insights and aligned on long-term technology strategies that support CUSTOMER's vision for digital transformation.

Based on these observations and in-depth discussions, Presidio has developed targeted recommendations aimed at improving the experience across all facets—agents, supervisors, and customers—while supporting CUSTOMER's strategic objectives. These recommendations focus on leveraging automation to streamline operations, enhance service delivery, and create more efficient workflows across the institution's key service areas.`,
  approach: `Our assessment methodology follows a structured approach:

1. Discovery & Process Identification - Collaborated with stakeholders to identify and document current contact center processes
2. Impact Analysis - Evaluated each process across four key dimensions: Productivity/Efficiency, Customer Experience, Agent Experience, and Compliance
3. Complexity Assessment - Analyzed technical complexity and implementation effort for each automation opportunity
4. Prioritization - Developed a prioritization matrix based on business impact versus implementation complexity
5. Recommendation Development - Created a strategic roadmap with phased implementation recommendations`,
  recommended_approach: `Based on our assessment, we recommend a phased approach to automation implementation:

Phase 1 - Quick Wins: Focus on high-impact, low-complexity processes that deliver immediate ROI and build organizational momentum for automation adoption.

Phase 2 - Strategic Initiatives: Address high-impact, higher-complexity processes that require more significant investment but deliver substantial long-term value.

Phase 3 - Optimization: Implement remaining automation opportunities and continuously optimize existing automations.

This phased approach allows for early wins, manageable change management, and sustained value delivery throughout the automation journey.`
};

// Default proposal team member
export const defaultTeamMember = {
  name: '',
  company: 'Presidio',
  title: '',
  email: '',
  phone: ''
};

// Default process structure
export const defaultProcess = {
  id: '',
  name: '',
  summary: '',
  impact_productivity: 'Medium-Low',
  importance_productivity: 'Medium-Low',
  impact_customer: 'Medium-Low',
  importance_customer: 'Medium-Low',
  impact_agent: 'Low',
  importance_agent: 'Low',
  impact_compliance: 'None',
  importance_compliance: 'None',
  complexity: 'Medium',
  total_score: 0
};
